import React, { useEffect, useState } from 'react';
import { Save, KeyRound } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const FIELDS = [
  ['hero_title_lead', 'Hero title (lead)', 'input'],
  ['hero_title_accent', 'Hero title (highlighted word)', 'input'],
  ['hero_title_tail', 'Hero title (tail)', 'input'],
  ['hero_sub', 'Hero subtitle', 'textarea'],
  ['mission_heading', 'Mission heading', 'input'],
  ['mission_text', 'Mission text', 'textarea'],
  ['cta_title', 'Closing CTA title', 'input'],
  ['cta_text', 'Closing CTA text', 'textarea'],
];

export const ContentManager = () => {
  const { toast } = useToast();
  const [content, setContent] = useState({});

  useEffect(() => { api.get('/content').then((r) => setContent(r.data || {})).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put('/content', content); toast({ title: 'Homepage updated' }); }
    catch { toast({ title: 'Save failed', variant: 'destructive' }); }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-sand p-6">
      <h3 className="font-display text-xl text-forest mb-1">Homepage text</h3>
      <p className="text-sm text-ink/60 mb-5">Edit the words that appear on the public homepage.</p>
      <div className="space-y-4">
        {FIELDS.map(([key, label, type]) => (
          <div key={key}>
            <label className="block text-xs font-600 text-ink/60 mb-1">{label}</label>
            {type === 'textarea' ? (
              <textarea rows={3} value={content[key] || ''} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none resize-none" />
            ) : (
              <input value={content[key] || ''} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
            )}
          </div>
        ))}
      </div>
      <button type="submit" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light"><Save className="w-4 h-4" /> Save homepage</button>
    </form>
  );
};

export const PasswordManager = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    try {
      await api.post('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      toast({ title: 'Password changed', description: 'Use your new password next time you sign in.' });
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast({ title: 'Could not change password', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-sand p-6 max-w-md">
      <h3 className="font-display text-xl text-forest mb-1 flex items-center gap-2"><KeyRound className="w-5 h-5 text-gold" /> Change password</h3>
      <p className="text-sm text-ink/60 mb-5">Update the password for your account.</p>
      <div className="space-y-4">
        <input required type="password" placeholder="Current password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
        <input required type="password" placeholder="New password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
        <input required type="password" placeholder="Confirm new password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" />
      </div>
      <button type="submit" className="mt-6 px-6 py-3 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light">Update password</button>
    </form>
  );
};
